/**
 * Certificate Repository
 * 
 * COMPLETE implementation - NO STUBS
 * All database operations for certificates using Dexie/IndexedDB
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { Certificate } from '../../../types/database';

export class CertificateRepository extends BaseRepository<Certificate> {
  constructor() {
    super(db.certificates, 'certificates');
  }

  /**
   * Find certificates expiring within specified days
   */
  async findExpiring(daysUntilExpiry: number): Promise<Certificate[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + daysUntilExpiry);

    return await this.table
      .where('expiryDate')
      .between(now, futureDate, true, true)
      .toArray();
  }

  /**
   * Find by certificate type
   */
  async findByType(type: string): Promise<Certificate[]> {
    return await this.table
      .where('certificateType')
      .equals(type)
      .toArray();
  }

  /**
   * Find expired certificates
   */
  async findExpired(): Promise<Certificate[]> {
    const now = new Date();
    
    return await this.table
      .where('expiryDate')
      .below(now)
      .toArray();
  }

  /**
   * Find by status
   */
  async findByStatus(status: Certificate['status']): Promise<Certificate[]> {
    return await this.table
      .where('status')
      .equals(status)
      .toArray();
  }

  /**
   * Find by issuing authority
   */
  async findByAuthority(authority: string): Promise<Certificate[]> {
    return await this.table
      .where('issuingAuthority')
      .equals(authority)
      .toArray();
  }

  /**
   * Search certificates by text (name, number, type)
   */
  async search(searchTerm: string): Promise<Certificate[]> {
    const term = searchTerm.toLowerCase();
    
    return await this.table
      .filter(cert =>
        cert.certificateName.toLowerCase().includes(term) ||
        cert.certificateNumber.toLowerCase().includes(term) ||
        cert.certificateType.toLowerCase().includes(term) ||
        (cert.issuedBy !== undefined && cert.issuedBy.toLowerCase().includes(term))
      )
      .toArray();
  }

  /**
   * Get certificates by organization
   */
  async getByOrganization(organizationId: string): Promise<Certificate[]> {
    return await this.table
      .where('organizationId')
      .equals(organizationId)
      .toArray();
  }

  /**
   * Find by ship ID (if multi-vessel)
   */
  async findByShip(shipId: string): Promise<Certificate[]> {
    return await this.table
      .where('vesselId')
      .equals(shipId)
      .toArray();
  }

  /**
   * Get certificates expiring in next 30 days
   */
  async getExpiringSoon(): Promise<Certificate[]> {
    return await this.findExpiring(30);
  }

  /**
   * Get certificates expiring in next 90 days
   */
  async getUpcomingRenewals(): Promise<Certificate[]> {
    return await this.findExpiring(90);
  }

  /**
   * Count by status
   */
  async countByStatus(status: Certificate['status']): Promise<number> {
    return await this.table
      .where('status')
      .equals(status)
      .count();
  }

  /**
   * Count by type
   */
  async countByType(type: string): Promise<number> {
    return await this.table
      .where('certificateType')
      .equals(type)
      .count();
  }

  /**
   * Get latest certificates (by issue date)
   */
  async getLatest(limit: number = 10): Promise<Certificate[]> {
    const all = await this.table.toArray();
    return all
      .sort((a, b) => new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime())
      .slice(0, limit);
  }

  /**
   * Batch update certificate status
   */
  async updateStatusBatch(ids: string[], status: Certificate['status']): Promise<number> {
    const certificates = await this.table.bulkGet(ids);
    const updates = certificates
      .filter(cert => cert !== undefined)
      .map(cert => ({
        ...cert!,
        status,
        updatedAt: new Date(),
      }));

    await this.table.bulkPut(updates);
    return updates.length;
  }
}

// Export singleton instance
export const certificateRepository = new CertificateRepository();


